/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Emitter from '@exc/emitter';
import type {PollingConfiguration, UserActivityEmitter, UserActivityMonitor} from '@adobe/exc-app/internal';
import {throttleQueue} from '@exc/shared';

const INACTIVITY_THRESHOLD = 5 * 60; // 5 minutes.

class UserActivityMonitorImpl extends Emitter implements UserActivityMonitor {
  private _active = true;
  private _inactivityTimeoutHandle = 0;
  private _inactivityTimeoutSec = INACTIVITY_THRESHOLD;
  // This value is negative so that we know if the user was ever active.
  private _lastActive = -1;
  private readonly setInactivityTimeout: () => void;
  private readonly document: Document;

  constructor(window: Window) {
    super();
    const {clearTimeout, document, setTimeout} = window;
    this.document = document;
    document.addEventListener('visibilitychange', () => this.onVisibilityChange());
    this.setInactivityTimeout = throttleQueue(() => {
      this._inactivityTimeoutHandle && clearTimeout(this._inactivityTimeoutHandle);
      if (this._active) {
        this._inactivityTimeoutHandle = setTimeout(() => this.onInactive(), this._inactivityTimeoutSec * 1000);
      }
    }, 1000, true, window);
    this.onActive();
  }

  public configure({inactivityTimeoutSec}: PollingConfiguration): void {
    if (inactivityTimeoutSec && inactivityTimeoutSec !== this._inactivityTimeoutSec) {
      this._inactivityTimeoutSec = inactivityTimeoutSec;
      if (this._active) {
        this.setInactivityTimeout();
      }
    }
  }

  private onActive() {
    this._lastActive = Date.now();
    this.setInactivityTimeout();
    if (!this._active) {
      this._active = true;
      this.emit('active');
    }
  }

  private onInactive() {
    if (this._active) {
      this._active = false;
      this.emit('inactive');
    }
  }

  private onVisibilityChange() {
    if (this.document.hidden) {
      this.onInactive();
      this.emit('hidden');
    } else {
      this.onActive();
      this.emit('shown');
    }
  }

  registerActivityEmitter(emitter: UserActivityEmitter): void {
    emitter.activityEvents && emitter.activityEvents.forEach(eventName => {
      emitter.on(eventName, () => this.onActive());
    });
  }

  public get active(): boolean {
    return this._active;
  }

  public get hidden(): boolean {
    return this.document.hidden;
  }

  public get lastActive(): number {
    return this._lastActive;
  }
}

export default (win: Window): UserActivityMonitor => new UserActivityMonitorImpl(win);
